/**
 * Flow Diagram Component
 * Node-RED style visualization of Volcano workflows
 */

import React, { useMemo } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Clock, Zap } from 'lucide-react';

interface FlowDiagramProps {
  workflow: {
    steps: Array<{
      name?: string;
      provider: string;
      model?: string;
      prompt: string;
      mcps?: string[];
      timeout?: number;
    }>;
    metadata?: {
      estimated_cost?: string;
    };
  };
  agentName: string;
  onNodeClick?: (stepIndex: number) => void;
}

// Custom node component for workflow steps
const StepNode = ({ data }: any) => {
  const providerColors = {
    anthropic: {
      bg: 'bg-orange-50',
      border: 'border-orange-300',
      text: 'text-orange-700',
      badge: 'bg-orange-100'
    },
    openai: {
      bg: 'bg-green-50',
      border: 'border-green-300',
      text: 'text-green-700',
      badge: 'bg-green-100'
    }
  };

  const colors = providerColors[data.provider as keyof typeof providerColors] || providerColors.anthropic;

  return (
    <div
      className={`${colors.bg} ${colors.border} border-2 rounded-lg p-4 min-w-[280px] shadow-md hover:shadow-lg transition-shadow cursor-pointer`}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className={`font-semibold ${colors.text}`}>
          {data.label}
        </h4>
        <Badge className={`${colors.badge} text-xs`}>
          {data.provider}
        </Badge>
      </div>

      {data.model && (
        <p className="text-xs text-gray-600 mb-2">{data.model}</p>
      )}

      <div className="text-xs text-gray-700 mb-3 line-clamp-2">
        {data.prompt}
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-600">
        {data.timeout && (
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{data.timeout / 1000}s</span>
          </div>
        )}
        {data.mcps && data.mcps.length > 0 && (
          <div className="flex items-center gap-1">
            <Zap className="w-3 h-3" />
            <span>{data.mcps.length} MCPs</span>
          </div>
        )}
      </div>

      {data.estimated_cost && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <div className="flex items-center gap-1 text-xs font-medium text-green-600">
            <DollarSign className="w-3 h-3" />
            <span>{data.estimated_cost}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Node types
const nodeTypes = {
  stepNode: StepNode
};

export default function FlowDiagram({ workflow, agentName, onNodeClick }: FlowDiagramProps) {
  const { nodes, edges } = useMemo(() => {
    if (!workflow?.steps || workflow.steps.length === 0) {
      return { nodes: [], edges: [] };
    }

    const stepNodes: Node[] = [];
    const stepEdges: Edge[] = [];

    // Start node
    stepNodes.push({
      id: 'start',
      type: 'input',
      data: { label: '🎯 Start' },
      position: { x: 100, y: 50 },
      style: {
        background: '#EFF6FF',
        border: '2px solid #93C5FD',
        borderRadius: '8px',
        padding: '10px',
        fontSize: '14px',
        fontWeight: 'bold'
      }
    });

    // Step nodes
    workflow.steps.forEach((step, index) => {
      const yOffset = 200 + (index * 180);
      
      stepNodes.push({
        id: `step-${index}`,
        type: 'stepNode',
        data: {
          label: step.name || `Step ${index + 1}`,
          provider: step.provider,
          model: step.model,
          prompt: step.prompt,
          mcps: step.mcps,
          timeout: step.timeout,
          estimated_cost: index === 0 ? workflow.metadata?.estimated_cost : null
        },
        position: { x: 100, y: yOffset }
      });

      // Edge from previous step or start
      const sourceId = index === 0 ? 'start' : `step-${index - 1}`;
      stepEdges.push({
        id: `edge-${sourceId}-step-${index}`,
        source: sourceId,
        target: `step-${index}`,
        type: 'smoothstep',
        animated: true,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#6B7280'
        },
        style: {
          stroke: '#6B7280',
          strokeWidth: 2
        }
      });
    });

    // End node
    const finalYOffset = 200 + (workflow.steps.length * 180);
    stepNodes.push({
      id: 'end',
      type: 'output',
      data: { label: '✅ Complete' },
      position: { x: 100, y: finalYOffset },
      style: {
        background: '#ECFDF5',
        border: '2px solid #6EE7B7',
        borderRadius: '8px',
        padding: '10px',
        fontSize: '14px',
        fontWeight: 'bold'
      }
    });

    // Edge to end
    stepEdges.push({
      id: `edge-step-${workflow.steps.length - 1}-end`,
      source: `step-${workflow.steps.length - 1}`,
      target: 'end',
      type: 'smoothstep',
      animated: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#6B7280'
      },
      style: {
        stroke: '#6B7280',
        strokeWidth: 2
      }
    });

    return { nodes: stepNodes, edges: stepEdges };
  }, [workflow]);

  if (nodes.length === 0) {
    return (
      <div className="h-[500px] bg-gray-50 rounded-lg flex items-center justify-center">
        <p className="text-gray-500">No workflow to display</p>
      </div>
    );
  }

  return (
    <div className="h-[600px] bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.5}
        maxZoom={1.5}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: true
        }}
      >
        <Background color="#94a3b8" gap={16} />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            if (node.type === 'input') return '#93C5FD';
            if (node.type === 'output') return '#6EE7B7';
            return '#FED7AA';
          }}
          nodeStrokeWidth={3}
          zoomable
          pannable
        />
      </ReactFlow>
    </div>
  );
}

